import {Component, computed, effect, inject, input, Signal, signal} from '@angular/core';
import {
  animalLowVoltageThreshold,
  animalMidVoltageThreshold
} from "src/app/presentation/common/asset-details.component";
import {Animal, Pregnancy} from 'src/app/infrastructure/moovement-api/models/asset';
import {DecimalPipe, SlicePipe} from '@angular/common';
import {DeviceInRangeStore} from 'src/app/domain/devices/device-in-range.store';
import {batteryIcon, batteryLevelColor} from 'src/app/domain/assets/battery/battery';
import {AggregatedAssetStateStore} from 'src/app/domain/assets/aggregated-state/aggregated-asset-state.store';
import {AnimalsStore} from 'src/app/domain/assets/animals/animals.store';
import {DeviceStore} from 'src/app/domain/devices/device.store';
import {DeviceType} from 'src/app/domain/devices/device';
import {AggregatedDeviceStateStore} from 'src/app/domain/devices/aggregated-state/aggregated-device-state.store';
import {PregnancyService} from "src/app/domain/services/pregnancy.service";

@Component({
  imports: [
    DecimalPipe,
    SlicePipe
  ],
  selector: 'app-animal-details-bar',
  templateUrl: './animal-details-bar.component.html',
  styleUrl: './animal-details-bar.component.css'
})
export class AnimalDetailsBarComponent {

  animal = input<Animal>();
  private readonly pregnancyService = inject(PregnancyService);

  currentPregnancy: Signal<Pregnancy> = computed(() => this.pregnancyService.get(this.animal()?.id)?.() ?? {chance: 0.0})
  observingDeviceId = input<string | undefined>();

  showBatteryLevel = input(false);
  showBatteryData = signal(false);
  batteryVoltage = signal<number | undefined>(undefined);
  batterLevelColor = signal('color-nominal');
  batteryIcon = signal('fa-solid fa-battery-full');

  deviceId = signal<string | undefined>(undefined);

  type = signal<string | undefined>(undefined);
  yearOfBirth = signal<string | undefined>(undefined);
  rssi = signal<number | undefined>(undefined);

  private readonly aggregatedAssetStateStore = inject(AggregatedAssetStateStore);
  private readonly aggregatedDeviceStateStore = inject(AggregatedDeviceStateStore);
  private readonly animalsStore = inject(AnimalsStore);
  private readonly deviceStore = inject(DeviceStore);
  private readonly deviceInRangeStore = inject(DeviceInRangeStore);

  constructor() {
    effect(() => {
      const id = this.animal()?.id;
      if (!id) return;

      const aggregatedAnimalState = this.aggregatedAssetStateStore.entityMap()[id];

      const batteryVoltage = aggregatedAnimalState?.batteryVoltage;

      this.batteryVoltage.set(batteryVoltage);
      this.batterLevelColor.set(batteryLevelColor(batteryVoltage, animalLowVoltageThreshold, animalMidVoltageThreshold));
      this.batteryIcon.set(batteryIcon(batteryVoltage, animalLowVoltageThreshold, animalMidVoltageThreshold));
    });

    effect(() => {
      const animal = this.animal();
      if (!animal) return;

      this.yearOfBirth.set(animal.yearOfBirth);
      this.type.set(animal.type);
    });

    effect(() => {
      const id = this.animal()?.id;
      if (!id) return;

      const deviceId = this.animalsStore.animalIdToDeviceId().get(id);
      this.deviceId.set(deviceId);
    });

    effect(() => {
      const deviceId = this.deviceId();
      const observingDeviceId = this.observingDeviceId();
      if (deviceId && observingDeviceId) {
        const observingDeviceState = this.aggregatedDeviceStateStore.entityMap()[observingDeviceId];
        if (!observingDeviceState) return;

        const rssi = observingDeviceState.lastRssiPerDevice.get(deviceId);
        this.rssi.set(rssi);
      } else if (deviceId) {
        const deviceInRange = this.deviceInRangeStore.devicesInRange().get(deviceId);
        this.rssi.set(deviceInRange?.rssi);
      }
    });

    effect(() => {
      const deviceId = this.deviceId();
      if (!deviceId) return;

      const deviceType = this.deviceStore.deviceIdToType().get(deviceId);
      if (deviceType && [DeviceType.GPS_PROXIMITY_ACCELEROMETER_TAG, DeviceType.GPS_TAG].includes(deviceType))
        this.showBatteryData.set(true);
    });
  }
}
